import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  TextField,
  InputAdornment,
  Avatar,
  Divider,
  Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Search, Chat, Person, DirectionsCar, Delete } from '@mui/icons-material';
import apiService from '../services/apiService';

const ChatConversationsPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getChatConversations();

      if (response.success) {
        setConversations(response.data || []);
      } else {
        setError(response.message || 'Failed to load chat conversations');
      }
    } catch (err) {
      console.error('Chat conversations error:', err);
      setError('Failed to load chat conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleViewConversation = async (ride) => {
    setSelectedConversation(ride);
    setOpenDialog(true);
    setMessagesLoading(true);

    try {
      const response = await apiService.getRideChatMessages(ride.ride_id);
      if (response.success) {
        setMessages(response.data || []);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDeleteConversation = async (rideId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This will remove all chat messages for this ride.')) {
      return;
    }

    try {
      const response = await apiService.deleteChatConversation(rideId);
      if (response.success) {
        setConversations(conversations.filter((conv) => conv.ride_id !== rideId));
        alert('Conversation deleted successfully');
      } else {
        alert(response.message || 'Failed to delete conversation');
      }
    } catch (err) {
      console.error('Delete conversation error:', err);
      alert('Failed to delete conversation');
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.crn?.toLowerCase().includes(search) ||
      conv.ride_id?.toLowerCase().includes(search) ||
      conv.passenger_name?.toLowerCase().includes(search) ||
      conv.driver_name?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      field: 'crn',
      headerName: 'CRN',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={12} fontWeight={600}>
          {params.row.crn || params.row.ride_id?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      field: 'passenger_name',
      headerName: 'Passenger',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
            <Person fontSize="small" />
          </Avatar>
          <Typography variant="body2">{params.row.passenger_name || 'N/A'}</Typography>
        </Box>
      ),
    },
    {
      field: 'driver_name',
      headerName: 'Driver',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
            <DirectionsCar fontSize="small" />
          </Avatar>
          <Typography variant="body2">{params.row.driver_name || 'N/A'}</Typography>
        </Box>
      ),
    },
    {
      field: 'message_count',
      headerName: 'Messages',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={<Chat fontSize="small" />}
          label={params.row.message_count || 0}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'last_message_at',
      headerName: 'Last Message',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(params.row.last_message_at)}
        </Typography>
      ),
    },
    {
      field: 'ride_status',
      headerName: 'Ride Status',
      width: 130,
      renderCell: (params) => {
        const status = params.row.ride_status;
        let color = 'default';
        if (status === 'completed') color = 'success';
        else if (status === 'cancelled') color = 'error';
        else if (status === 'in_progress') color = 'warning';

        return (
          <Chip
            label={status?.replace('_', ' ') || 'N/A'}
            size="small"
            color={color}
            sx={{ textTransform: 'capitalize' }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleViewConversation(params.row)}
            color="primary"
            title="View Conversation"
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteConversation(params.row.ride_id)}
            color="error"
            title="Delete Conversation"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Chat Conversations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all chat conversations between drivers and passengers
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search by CRN, passenger or driver name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <DataGrid
            rows={filteredConversations}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            getRowId={(row) => row.ride_id}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Conversation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Chat Conversation</Typography>
              <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                CRN: {selectedConversation?.crn || selectedConversation?.ride_id?.substring(0, 8) + '...'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip
                icon={<Person fontSize="small" />}
                label={selectedConversation?.passenger_name || 'Passenger'}
                color="secondary"
                size="small"
              />
              <Chip
                icon={<DirectionsCar fontSize="small" />}
                label={selectedConversation?.driver_name || 'Driver'}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: '#f5f5f5', p: 2 }}>
          {messagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>Loading messages...</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chat sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography color="text.secondary">No messages in this conversation</Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map((msg, index) => {
                const isDriver = msg.sender_type === 'driver';
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: isDriver ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        maxWidth: '70%',
                        p: 1.5,
                        bgcolor: isDriver ? 'primary.light' : 'secondary.light',
                        borderRadius: 2,
                        borderBottomLeftRadius: isDriver ? 0 : 16,
                        borderBottomRightRadius: isDriver ? 16 : 0,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: isDriver ? 'primary.main' : 'secondary.main' }}>
                          {isDriver ? <DirectionsCar sx={{ fontSize: 12 }} /> : <Person sx={{ fontSize: 12 }} />}
                        </Avatar>
                        <Typography variant="caption" fontWeight="bold" color={isDriver ? 'primary.dark' : 'secondary.dark'}>
                          {isDriver ? 'Driver' : 'Passenger'}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{msg.message}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                        {formatDate(msg.created_at)}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatConversationsPage;
